import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux/store';
import { SYNC_STATUS_QUERY_KEY, useSATConnection } from '../../sat-connection';
import { fetchRegularizationOffer } from './offer.service';
import type { DeclaredDebt, RegularizationOffer } from './types';

/** The measured offer is keyed by user; a declared one adds what was declared. */
export const regularizationOfferKey = (
	userId: number | undefined,
	declared?: DeclaredDebt | null,
) => ['regularization-offer', userId, declared ?? null] as const;

/** While the engine is still calculating (`not_ready`) it is asked again every 30 s. */
const NOT_READY_POLL_MS = 30_000;

const OFFER_QUERY = {
	// The offer and its breakdown mount apart, and the banner, the score and the
	// package read the same debt: one quote per session, not one per screen.
	staleTime: 5 * 60 * 1000,
	retry: 1,
	refetchOnWindowFocus: false,
	refetchInterval: (query: { state: { data?: RegularizationOffer } }) =>
		query.state.data?.status === 'not_ready' ? NOT_READY_POLL_MS : false,
	refetchIntervalInBackground: false,
} as const;

export type OfferQuery = {
	/** The engine's answer; undefined while there is none. */
	offer: RegularizationOffer | undefined;
	isLoading: boolean;
	/** The request failed (network, session). An upstream failure is an offer with status `retry`. */
	isError: boolean;
	retry: () => void;
};

export type MeasuredOffer = OfferQuery & {
	/**
	 * The SAT linking (`web_linking`) or its sync (`web_syncing`) is running,
	 * the "Sincronizando" bar: the engine is not even asked yet, and the
	 * screens say the debt is being calculated. Null otherwise.
	 */
	satSyncing: 'web_linking' | 'web_syncing' | null;
};

/**
 * The linked user's offer, measured against the SAT. It is the web's ONE
 * source of "how much is owed": Declaraciones, the score, the campaign link,
 * the package and its events read it, so no screen counts periods or decides a
 * status on its own. Unlinked users quote what they declare
 * (`useDeclaredOffer`).
 *
 * Only selling surfaces ask (`fetch: false` reads what is already cached).
 */
export const useMeasuredOffer = ({
	enabled = true,
	fetch = true,
}: { enabled?: boolean; fetch?: boolean } = {}): MeasuredOffer => {
	const userId = useSelector((state: RootState) => state.profile.user?.id);
	const { isLinked, status } = useSATConnection();
	const queryClient = useQueryClient();
	const requested = enabled && !!userId;
	const satSyncing =
		requested && status === 'linking'
			? 'web_linking'
			: requested && status === 'syncing'
				? 'web_syncing'
				: null;
	const active = requested && isLinked && !satSyncing;
	const shouldFetch = active && fetch;

	const query = useQuery({
		queryKey: regularizationOfferKey(userId),
		queryFn: () => fetchRegularizationOffer(),
		enabled: shouldFetch,
		...OFFER_QUERY,
	});

	// While the engine calculates, the sync is refreshed too: once a download
	// shows up the "Sincronizando" bar takes over and the engine is not asked
	// again until it ends.
	const engineCalculating = active && query.data?.status === 'not_ready';
	useEffect(() => {
		if (!engineCalculating) return;
		void queryClient.invalidateQueries(
			{ queryKey: SYNC_STATUS_QUERY_KEY },
			{ cancelRefetch: false },
		);
	}, [engineCalculating, query.dataUpdatedAt, queryClient]);

	const { refetch } = query;
	const retry = useCallback(() => void refetch(), [refetch]);
	return {
		offer: active ? query.data : undefined,
		satSyncing,
		isLoading: shouldFetch && query.isLoading,
		isError: shouldFetch && query.isError && !query.data,
		retry,
	};
};

/** The offer of what an unlinked user declares; nothing is asked without a declaration. */
export const useDeclaredOffer = (declared: DeclaredDebt | null): OfferQuery => {
	const userId = useSelector((state: RootState) => state.profile.user?.id);
	const query = useQuery({
		queryKey: regularizationOfferKey(userId, declared),
		queryFn: () => fetchRegularizationOffer(declared ?? undefined),
		enabled: !!userId && !!declared,
		...OFFER_QUERY,
	});
	const { refetch } = query;
	const retry = useCallback(() => void refetch(), [refetch]);
	return {
		offer: query.data,
		isLoading: !!declared && query.isLoading,
		isError: query.isError && !query.data,
		retry,
	};
};
